import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { stats } from '../../data/mock';
import './CinematicHero.css';

const DESKTOP_SRC = '/media/hero-scrub.mp4';
const MOBILE_SRC = '/media/hero-scrub-m.mp4';
const POSTER = '/media/hero-poster.jpg';
const FALLBACK_DURATION = 5.04;

// Chapters tell the story in the page's own section order; each fades in,
// holds, and fades out inside its window of the film.
const CHAPTERS = [
  { index: '01', label: 'Every missed call costs you', from: 0.07, to: 0.27 },
  { index: '02', label: 'AI that answers in seconds', from: 0.3, to: 0.5 },
  { index: '03', label: 'Live in 7–14 days', from: 0.53, to: 0.73 },
  { index: '04', label: 'Results you can count', from: 0.76, to: 0.9 },
];

const HEADLINE_WORDS = ['Calls', 'Messages', 'Bookings', 'Revenue'];

const CinematicHero = () => {
  const trackRef = useRef(null);
  const videoRef = useRef(null);
  const contentRef = useRef(null);
  const ctaRef = useRef(null);
  const progressRef = useRef(null);
  const chapterRefs = useRef([]);
  const durationRef = useRef(FALLBACK_DURATION);

  const [source, setSource] = useState(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [currentWord, setCurrentWord] = useState(0);

  useEffect(() => {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileQuery = window.matchMedia('(max-width: 768px)');
    const apply = () => {
      setReducedMotion(motionQuery.matches);
      setSource(mobileQuery.matches ? MOBILE_SRC : DESKTOP_SRC);
    };
    apply();
    motionQuery.addEventListener('change', apply);
    mobileQuery.addEventListener('change', apply);
    return () => {
      motionQuery.removeEventListener('change', apply);
      mobileQuery.removeEventListener('change', apply);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % HEADLINE_WORDS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (reducedMotion) return undefined;
    let raf;
    let current = 0;

    const tick = () => {
      const track = trackRef.current;
      const video = videoRef.current;
      if (track) {
        const rect = track.getBoundingClientRect();
        const scrollable = rect.height - window.innerHeight;
        const progress = scrollable > 0
          ? Math.min(1, Math.max(0, -rect.top / scrollable))
          : 0;

        if (video && video.readyState >= 2) {
          // ease toward the scroll position instead of setting it directly —
          // this is what makes the scrub feel weighted
          const target = progress * durationRef.current;
          current += (target - current) * 0.12;
          if (Math.abs(target - current) > 0.008) {
            video.currentTime = current;
          }
          // crossfade the static hero into the film over the first few percent
          video.style.opacity = String(Math.min(1, progress / 0.05));
        }

        if (contentRef.current) {
          const out = Math.min(1, progress / 0.14);
          contentRef.current.style.opacity = String(1 - out);
          contentRef.current.style.transform = `translateY(${-40 * out}px)`;
          contentRef.current.style.pointerEvents = out > 0.6 ? 'none' : 'auto';
        }

        chapterRefs.current.forEach((el, i) => {
          if (!el) return;
          const { from, to } = CHAPTERS[i];
          const t = (progress - from) / (to - from);
          const opacity = t <= 0 || t >= 1 ? 0 : Math.min(1, Math.min(t, 1 - t) * 4);
          el.style.opacity = String(opacity);
          el.style.transform = `translateY(${(1 - opacity) * 12}px)`;
        });

        if (ctaRef.current) {
          const t = Math.max(0, Math.min(1, (progress - 0.9) / 0.1));
          ctaRef.current.style.opacity = String(t);
          ctaRef.current.style.pointerEvents = t > 0.5 ? 'auto' : 'none';
        }

        if (progressRef.current) {
          progressRef.current.style.transform = `scaleX(${progress})`;
        }
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reducedMotion]);

  const content = (
    <div className="cin-hero__content" ref={reducedMotion ? null : contentRef}>
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-8">
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <span className="text-emerald-400 text-sm font-medium">
          Nigeria's AI Growth &amp; Automation Partner
        </span>
      </div>

      <h1 className="font-bold text-white leading-tight mb-6" style={{ fontSize: 'clamp(2.3rem, 4.6vw, 4rem)' }}>
        Stop Losing Customers to{' '}
        <span className="text-gray-400">Missed Calls</span>
        <br />
        <span className="text-emerald-400">AI That Answers</span> Every{' '}
        <span className="cin-headline-rotator relative">
          {/* Sizers: every candidate word, stacked in the same grid cell and
              hidden, so the box keeps the width of the longest one. Without
              them a longer word re-wraps the line and changes the h1's height. */}
          {HEADLINE_WORDS.map((word) => (
            <span key={`sizer-${word}`} aria-hidden="true" className="invisible">
              {word}
            </span>
          ))}
          {/* The underline lives inside the visible word so it tracks that
              word's width rather than the reserved (longest) width. */}
          <span key={currentWord} className="relative animate-fade-in">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-amber-400">
              {HEADLINE_WORDS[currentWord]}
            </span>
            <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-amber-400 rounded-full" />
          </span>
        </span>
      </h1>

      <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-xl">
        We build AI receptionists, AI employees and WhatsApp automation for Nigerian
        businesses — answering every call and message in seconds, booking
        appointments, and following up until the deal is done.
        <span className="text-white font-medium"> 24/7. In naira.</span>
      </p>

      <div className="flex flex-col sm:flex-row gap-4 mb-2">
        <Link to="/contact">
          <Button
            size="lg"
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-500/40 hover:scale-105 w-full sm:w-auto"
          >
            Get Free Strategy Session
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
        <Link to="/how-it-works">
          <Button
            variant="outline"
            size="lg"
            className="border-gray-700 text-white hover:bg-white/5 font-semibold px-8 py-6 text-lg rounded-xl w-full sm:w-auto"
          >
            Watch How It Works
          </Button>
        </Link>
      </div>

      <div className="cin-stat-strip">
        {stats.map((stat) => (
          <div key={stat.label} className="cin-stat-strip__item">
            <div className="cin-stat-strip__value">{stat.value}</div>
            <div className="cin-stat-strip__label">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section
      className="cin-hero"
      ref={trackRef}
      aria-label="Dependify — AI that answers every call"
    >
      <div className="cin-hero__stage">
        <img className="cin-hero__poster" src={POSTER} alt="" />

        {!reducedMotion && source && (
          <video
            ref={videoRef}
            className="cin-hero__video"
            src={source}
            poster={POSTER}
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
            onLoadedMetadata={(e) => {
              if (e.currentTarget.duration && Number.isFinite(e.currentTarget.duration)) {
                durationRef.current = e.currentTarget.duration;
              }
            }}
          />
        )}

        <div className="cin-hero__scrim" />

        <div className="cin-hero__inner">{content}</div>

        {!reducedMotion && (
          <>
            <div className="cin-hero__chapters" aria-hidden="true">
              <div className="cin-hero__chapters-inner">
                {CHAPTERS.map((chapter, i) => (
                  <div
                    key={chapter.index}
                    className="cin-chapter"
                    ref={(el) => { chapterRefs.current[i] = el; }}
                  >
                    <span className="cin-chapter__index">{chapter.index}</span>
                    <span className="cin-chapter__rule" />
                    <span className="cin-chapter__label">{chapter.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="cin-hero__cta" ref={ctaRef}>
              <div className="cin-hero__cta-card">
                <div className="cin-hero__cta-inner">
                  <h3 className="cin-hero__cta-title">Stop losing customers this week.</h3>
                  <p className="cin-hero__cta-sub">
                    Free strategy session — we'll put a naira figure on what missed
                    calls and slow replies are costing your business.
                  </p>
                  <div>
                    <Link to="/contact">
                      <Button className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-5 rounded-xl shadow-lg shadow-emerald-500/25">
                        Get Free Strategy Session
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="cin-hero__scrollhint">Scroll</div>
            <div className="cin-hero__progress" ref={progressRef} />
          </>
        )}
      </div>
    </section>
  );
};

export default CinematicHero;
